import type { ReactNode } from 'react';
import { AlertCircle, Inbox, Loader2, type LucideIcon } from 'lucide-react';
import { Skeleton } from './ui/skeleton';
import { cn } from './ui/utils';

type StatePanelState = 'loading' | 'empty' | 'error';
type StatePanelSize = 'default' | 'compact';
type StatePanelAlign = 'center' | 'left';

interface StatePanelProps {
  state: StatePanelState;
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
  className?: string;
  size?: StatePanelSize;
  align?: StatePanelAlign;
}

const DEFAULT_ICONS: Record<StatePanelState, LucideIcon> = {
  loading: Loader2,
  empty: Inbox,
  error: AlertCircle,
};

const CONTAINER_STYLES: Record<StatePanelState, string> = {
  loading: 'border-border/60 bg-muted/20',
  empty: 'border-dashed border-border/60 bg-muted/10',
  error: 'border-destructive/25 bg-destructive/5',
};

const ICON_STYLES: Record<StatePanelState, string> = {
  loading: 'border-primary/20 bg-primary/10 text-primary',
  empty: 'border-border/60 bg-background text-muted-foreground',
  error: 'border-destructive/20 bg-destructive/10 text-destructive',
};

export function StatePanel({
  state,
  title,
  description,
  icon,
  action,
  className,
  size = 'default',
  align = 'center',
}: StatePanelProps) {
  const Icon = icon ?? DEFAULT_ICONS[state];
  const isLoading = state === 'loading';
  const isCompact = size === 'compact';
  const isCentered = align === 'center';

  return (
    <div
      role={state === 'error' ? 'alert' : 'status'}
      aria-live={state === 'error' ? 'assertive' : 'polite'}
      className={cn(
        'flex rounded-xl border',
        isCentered ? 'flex-col items-center justify-center text-center' : 'flex-col items-start justify-center text-left',
        isCompact ? 'min-h-[144px] gap-3 px-4 py-6' : 'min-h-[220px] gap-4 px-6 py-10',
        CONTAINER_STYLES[state],
        className,
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full border',
          isCompact ? 'h-10 w-10' : 'h-12 w-12',
          ICON_STYLES[state],
        )}
      >
        <Icon className={cn(isCompact ? 'h-4 w-4' : 'h-5 w-5', isLoading && 'animate-spin')} />
      </div>

      <div className={cn('space-y-1', isCentered ? 'max-w-md' : 'max-w-lg')}>
        <h3 className={cn(isCompact ? 'text-sm font-semibold' : 'text-lg font-semibold')}>{title}</h3>
        {description ? (
          <p className={cn(isCompact ? 'text-xs' : 'text-sm', 'text-muted-foreground')}>{description}</p>
        ) : null}
      </div>

      {isLoading ? (
        <div className={cn('w-full max-w-xs space-y-2', !isCentered && 'max-w-sm')}>
          <Skeleton className={cn(isCompact ? 'h-2 w-24' : 'h-2.5 w-32', isCentered && 'mx-auto')} />
          <Skeleton className={cn(isCompact ? 'h-2 w-32' : 'h-2.5 w-48', isCentered && 'mx-auto')} />
        </div>
      ) : null}

      {action ? <div className={cn('pt-1', isCentered ? 'flex justify-center' : 'flex justify-start')}>{action}</div> : null}
    </div>
  );
}
